import {Grid} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {useRecoilState} from "recoil";
import "dayjs/locale/it";
import {SelectElementModelFE} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {FormExceptionListModel} from "../../../api/types/exceptionList";
import CustomBanner from "../../../common/components/CustomBanner";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {useFormContext} from "react-hook-form";
import DealerAndTenant from "../../../common/components/DealerAndTenant";

const FormExceptionList = ({
                               readOnly, inUse, commercialCategoryListOriginal
                           }: {
    readOnly: boolean, inUse: boolean, commercialCategoryListOriginal: CommercialCategorySelectFE[]
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [commercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use State
    const [commercialCategoryList, setCommercialCategoryList] = useState<SelectElementModelFE[]>(commercialCategoryListOriginal);

    // Use Form Context
    const form = useFormContext<FormExceptionListModel>();

    useEffect(() => {
        const value = form.watch("commercialSegmentId");
        if (value) {
            setCommercialCategoryList([...commercialCategoryListOriginal].filter(cc => cc.commercialSegment.id === (value as SelectElementModelFE).code));
        }
    }, []);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        form.setValue("commercialSegmentId", value);
        if (value) {
            setCommercialCategoryList([...commercialCategoryListOriginal].filter(cc => cc.commercialSegment.id === (value as SelectElementModelFE).code));
        } else {
            setCommercialCategoryList([...commercialCategoryListOriginal]);
        }
    };

    return (
        <>
            <Grid container spacing={2} alignItems="start">
                {inUse && <CustomBanner message={t("pricing:exceptionList.banner")} severity={"warning"}/>}
                <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                    {/* NAME ANCILLARY LIST */}
                    <Grid item xs={12} md={3}>
                        <FormTextField
                            {...form.register("name")}
                            label={`${t("pricing:rentalPeriods.form.labels.name")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* DESCRIPTION ANCILLARY LIST */}
                    <Grid item xs={12} md={3}>
                        <FormTextField
                            {...form.register("description")}
                            label={`${t("pricing:exceptionList.form.labels.description")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* COMMERCIAL SEGMENTS */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...form.register("commercialSegmentId")}
                            label={`${t("pricing:exceptionList.form.labels.commercialSegment")}*`}
                            options={commercialSegmentList}
                            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                            readOnly={readOnly || inUse}
                            handleChange={handleChangeCommercialSegment}
                        />
                    </Grid>

                    {/* COMMERCIAL CATEGORY */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...form.register("commercialCategoryId")}
                            label={`${t("pricing:exceptionList.form.labels.commercialCategory")}*`}
                            options={commercialCategoryList}
                            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* RANGE ANCILLARY LIST */}
                    {/* FROM */}
                    <Grid item xs={12} md={3}>
                        <FormDateTimePicker
                            {...form.register("from")}
                            label={`${t("pricing:exceptionList.form.labels.startDateAssociation")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* TO */}
                    <Grid item xs={12} md={3}>
                        <FormDateTimePicker
                            {...form.register("to")}
                            label={`${t("pricing:exceptionList.form.labels.endDateAssociation")}*`}
                            readOnly={readOnly}
                        />
                    </Grid>
                    <DealerAndTenant
                        readOnly={readOnly}
                        md={3}
                        legalEntityName={"legalEntity"}
                        dealerName={"dealer"}
                        dealerIsVisible={true}
                        idForReadOnly={form.getValues("id")}
                    />
                    {/* ancillaryPack */}
                    <Grid item xs={12} md={3}>
                        <FormCustomSwitch
                            {...form.register("ancillaryPack")}
                            label="Pack"
                            readOnly={readOnly || inUse || Boolean(form.getValues("id"))}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default FormExceptionList;
